//React
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Form } from 'react-bootstrap';
//API
import {
  HospitalApi,
  MemberApi,
  TemplateApi,
  WoundApi,
  GcodeApi,
} from '../../apis/index.js';
//Material-UI
import Grid from '@material-ui/core/Grid';
import Checkbox from '@mui/material/Checkbox';
import AutoComplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import { DataGrid } from '@mui/x-data-grid';
//Components
import { IOSSwitch } from '../../components/Switch.js';
import MyLoading from '../../components/Loading.js';
import BasicModal, {
  EmailModal,
  ConfirmModal,
} from '../../components/Modal.js';
import countryCodeName from '../../components/countryCode.js';

const HospitalUpdate = (props) => {
  const id = props.match.params.id;
  const [hospital, setHospital] = useState({
    imgUrl: '',
    emblemImgUrl: '',
    hospitalStatus: '',
  });
  const [email, setEmail] = useState('');
  const [tempCountryCode, setTempCountryCode] = useState('');
  const [emblem, setEmblem] = useState(null);
  const [woundType, setWoundType] = useState([]);
  const [templateList, setTemplateList] = useState([]);
  const settings = new Object();
  settings.togglenYn = false;
  settings.hiddenYn = false;
  settings.autoIssued = true;
  settings.aiDrawYn = true;
  settings.disableYn = false;
  settings.woundName = null;
  settings.kitMetaId = null;
  settings.woundListId = null;
  const [modalStatus, setModalStatus] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [emailModalStatus, setEmailModalStatus] = useState(false);
  const [emailModalContent, setEmailModalContent] = useState('');
  const [modalConfirmStatus, setConfirmModalStatus] = useState(false);
  const [modalConfirmContent, setConfirmModalContent] = useState('');
  const [reInviteIsActive, setReInviteIsActive] = useState(false);
  const [editEmailInfo, setEditEmailInfo] = useState(false);
  const [doctorList, setDoctorList] = useState([]);
  const fileInputRef = useRef(null);
  const [defaultTemplates, setDefaultTemplates] = useState({});
  const [deleteFlag, setDeleteFlag] = useState(false);
  const [formData, setFormData] = useState(new FormData());
  const [emblemBlobUrl, setEmblemBlobUrl] = useState('');

  const [loading, setLoading] = useState(false);

  // path에서 rokithc.com 이후의 경로만 추출
  const extractPathFromUrl = (url) => {
    if (!url) return '';
    const parts = url.split('rokithc.com');
    if (parts.length > 1) {
      const path = parts[parts.length - 1];
      // 앞의 '/' 제거
      return path.startsWith('/') ? path.substring(1) : path;
    }
    return url;
  };

  useEffect(() => {
    const fetchHospitalInfo = async () => {
      try {
        // Fetch hospital info
        const hospitalRes = await HospitalApi.getHospitalID(id);
        setHospital(hospitalRes.data);
        setEmail(hospitalRes.data.email);
        // Fetch wound list
        const woundRes = await WoundApi.getWoundList();
        const wound = woundRes.data.map((woundList) => ({
          woundListId: woundList.id,
          woundName: woundList.woundName,
        }));
        const setting = wound.map((item) => ({
          [item.woundListId]: { ...settings, ...item },
        }));
        const updatedArr = setting.map((item) => {
          const key = Object.keys(item)[0];
          const foundItem = hospitalRes.data.woundFlagRegisterList.find(
            (item1) => item1.woundListId.toString() === key,
          );

          return foundItem
            ? {
                [key]: {
                  ...item[key],
                  ...foundItem,
                  togglenYn: true,
                },
              }
            : item;
        });

        setWoundType(updatedArr);

        // Fetch template list
        const templateRes = await TemplateApi.getTemplate();
        setTemplateList(templateRes.data);

        // Fetch doctor list
        const doctorRes = await MemberApi.getMemberForHospital(id);
        setDoctorList(doctorRes.data);

        // Load emblem image with auth API
        if (hospitalRes.data.emblemImgUrl) {
          // Check if it's a GCS URL (contains rokithc.com) or base64 (data:)
          if (hospitalRes.data.emblemImgUrl.includes('rokithc.com')) {
            try {
              const path = extractPathFromUrl(hospitalRes.data.emblemImgUrl);
              const blob = await GcodeApi.getWoundPicture(path);
              const blobUrl = URL.createObjectURL(blob);
              setEmblemBlobUrl(blobUrl);
            } catch (error) {
              console.error('Failed to load emblem image:', error);
              setEmblemBlobUrl('');
            }
          } else if (hospitalRes.data.emblemImgUrl.startsWith('data:')) {
            // Base64 이미지는 그대로 사용
            setEmblemBlobUrl(hospitalRes.data.emblemImgUrl);
          } else {
            setEmblemBlobUrl(hospitalRes.data.emblemImgUrl);
          }
        }
      } catch (error) {
        console.error('Failed to fetch hospital data:', error);
      }
    };

    fetchHospitalInfo();
  }, [id]);

  useEffect(() => {
    setTempCountryCode(hospital.countryCode);
  }, [hospital.countryCode]);

  // 컴포넌트 언마운트 시 blob URL 정리
  useEffect(() => {
    return () => {
      if (emblemBlobUrl && !emblemBlobUrl.startsWith('data:')) {
        URL.revokeObjectURL(emblemBlobUrl);
      }
    };
  }, [emblemBlobUrl]);

  useEffect(() => {
    if (hospital.countryCode === 'USA') {
      setHospital((prevHospital) => ({
        ...prevHospital,
        productName: 'APLICOR 3D',
      }));
    } else if (hospital.countryCode) {
      setHospital((prevHospital) => ({
        ...prevHospital,
        productName: 'Dr. INVIVO',
      }));
    } else {
      setHospital((prevHospital) => ({
        ...prevHospital,
        productName: '',
      }));
    }
  }, [hospital.countryCode]);

  const templateName = useMemo(
    () =>
      templateList.map((template) => ({
        label: template.templateName,
        id: template.id,
      })),
    [templateList],
  );

  useEffect(() => {
    const newDefaultTemplates = {};
    woundType.forEach((woundList) => {
      const wound = woundList[Object.keys(woundList)[0]];
      const woundListId = Object.keys(woundList)[0];
      if (wound.kitMetaId == null) {
        newDefaultTemplates[woundListId] = null;
      } else {
        const matchedTemplate = templateName.find(
          (item) => item.id === wound.kitMetaId,
        );
        newDefaultTemplates[woundListId] = matchedTemplate || null;
      }
    });
    setDefaultTemplates(newDefaultTemplates);
  }, [templateName, woundType]);

  useEffect(() => {
    if (modalConfirmStatus === false) {
      if (formData.has('id')) {
        updateData();
      }
    }
  }, [formData]);

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 90,
    },
    {
      field: 'email',
      headerName: 'Doctor Email',
      width: 300,
    },
    {
      field: 'verifiedFlag',
      headerName: 'Status',
      width: 100,
    },
    {
      field: 'memberName',
      headerName: 'Name',
      width: 300,
    },
    {
      field: 'modifyDate',
      headerName: 'Modify Date',
      width: 300,
    },
  ];

  const addColumnsData = (columns) => {
    const addDesign = {
      sortable: false,
      headerAlign: 'center',
      align: 'center',
    };
    for (let i in columns) {
      columns[i] = { ...columns[i], ...addDesign };
    }
    return columns;
  };

  const addRowsData = (rows) => {
    return rows.map((row, index) => ({
      id: index + 1,
      ...row,
    }));
  };
  const handleRowSelection = (e) => {
    window.open('/updateMember/' + e.row.id);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setHospital((prevHospital) => ({
      ...prevHospital,
      [name]: value,
    }));
  };

  const handleChangeCountryCode = (e, value) => {
    if (value) {
      setHospital({
        ...hospital,
        countryCode: value.label,
      });
    } else {
      setHospital({
        ...hospital,
        countryCode: '',
      });
    }
  };

  const handleChangeWound = (e) => {
    const [woundListId, key] = e.target.name.split(',');
    setWoundType((prevWoundType) =>
      prevWoundType.map((wound) => {
        if (wound[woundListId]) {
          return {
            [woundListId]: {
              ...wound[woundListId],
              [key]: e.target.checked,
            },
          };
        }
        return wound;
      }),
    );
  };

  const handleChangeTemplate = (woundType) => (e, value) => {
    const [woundListId, key] = woundType.split(',');
    setWoundType((prevWoundType) =>
      prevWoundType.map((wound) => {
        if (wound[woundListId]) {
          return {
            [woundListId]: {
              ...wound[woundListId],
              [key]: value ? value.id : null,
            },
          };
        }
        return wound;
      }),
    );
  };

  const updateHospital = async (e) => {
    e.preventDefault();
    if (!hospital.hospitalName) {
      showModalWithContent('Please set Hospital Name.');
      handleModalOpen();
      return;
    }

    if (!hospital.countryCode) {
      showModalWithContent('Please select a country code.');
      handleModalOpen();
      return;
    }

    const woundData = woundType.filter(
      (wound) => wound[Object.keys(wound)[0]].togglenYn,
    );
    if (woundData.length === 0) {
      showModalWithContent('wound를 최소 하나 이상 선택해주세요.');
      handleModalOpen();
      return;
    }

    for (let i = 0; i < woundType.length; i++) {
      let key = Object.keys(woundType[i])[0];
      if (woundType[i][key].togglenYn === true) {
        if (woundType[i][key].kitMetaId == null) {
          showModalWithContent(`설정된 Template이 없습니다.`);
          handleModalOpen();
          return;
        }
        woundData.push(woundType[i]);
      }
    }

    const newFormData = new FormData();
    newFormData.append('id', hospital.hospitalId);
    newFormData.append('email', email?.toString().trim());
    newFormData.append('hospitalName', hospital.hospitalName);
    if (hospital.location) {
      newFormData.append('location', hospital.location);
    }
    newFormData.append('countryCode', hospital.countryCode);
    newFormData.append('productName', hospital.productName);
    newFormData.append('deleteFlag', deleteFlag);
    newFormData.append('emblem', emblem ? emblem : null);

    woundData.forEach((wound, i) => {
      const key = Object.keys(wound)[0];
      newFormData.append(
        `woundFlagRegisterList[${i}].hospitalId`,
        hospital.hospitalId,
      );
      newFormData.append(
        `woundFlagRegisterList[${i}].aiDrawYn`,
        wound[key].aiDrawYn,
      );
      newFormData.append(
        `woundFlagRegisterList[${i}].autoIssued`,
        wound[key].autoIssued,
      );
      newFormData.append(
        `woundFlagRegisterList[${i}].disableYn`,
        wound[key].disableYn,
      );
      newFormData.append(
        `woundFlagRegisterList[${i}].hiddenYn`,
        wound[key].hiddenYn,
      );
      newFormData.append(
        `woundFlagRegisterList[${i}].kitMetaId`,
        wound[key].kitMetaId,
      );
      newFormData.append(
        `woundFlagRegisterList[${i}].woundListId`,
        wound[key].woundListId,
      );
    });
    setFormData(newFormData);

    for (let item of hospital.woundFlagRegisterList) {
      const objWithWoundListId = woundType.find((obj) =>
        obj.hasOwnProperty(item.woundListId),
      );
      const checkToggleYn =
        objWithWoundListId && objWithWoundListId[item.woundListId].togglenYn;
      if (!checkToggleYn) {
        showConfirmModalWithContent(
          `삭제 되는 환부가 있습니다. 정말 삭제 하시겠습니까?`,
        );
        handleConfirmModalOpen();
        return;
      }
    }
  };

  // 병원 정보 수정 API 호출
  const updateData = async () => {
    setLoading(true);
    const res = await HospitalApi.updateHospital(formData);
    if (res.statusCode === 200) {
      props.history.push('/hospitalAdminList');
    } else {
      setLoading(false);
      alert(res.statusCode + '\n' + res.message);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    const reader = new FileReader();
    reader.onloadend = () => {
      setEmblem(file);
      const base64Url = reader.result;
      setHospital((prevHospital) => ({
        ...prevHospital,
        emblemImgUrl: base64Url,
      }));
      // Base64 이미지는 그대로 사용
      setEmblemBlobUrl(base64Url);
    };
    reader.readAsDataURL(file);
  };

  const deleteImage = () => {
    setDeleteFlag(true);
    setEmblem(null);
    // 기존 blob URL 정리
    if (emblemBlobUrl && !emblemBlobUrl.startsWith('data:')) {
      URL.revokeObjectURL(emblemBlobUrl);
    }
    setEmblemBlobUrl('');
    setHospital((prevHospital) => ({
      ...prevHospital,
      emblemImgUrl: '',
    }));
  };

  const handleModalOpen = () => {
    setModalStatus(true);
  };

  const handleModalClose = () => setModalStatus(false);

  const showModalWithContent = (content) => {
    setModalContent(content);
  };

  const handleConfirmModalOpen = () => {
    setConfirmModalStatus(true);
  };

  const handleConfirmModalClose = () => {
    setConfirmModalStatus(false);
  };

  const handleConfirmModalSet = async () => {
    setConfirmModalStatus(false);

    for (const item of hospital.woundFlagRegisterList) {
      const objWithWoundListId = woundType.find((obj) =>
        obj.hasOwnProperty(item.woundListId),
      );
      if (
        objWithWoundListId &&
        !objWithWoundListId[item.woundListId].togglenYn
      ) {
        const res = await HospitalApi.woundFlagDelete(
          hospital.hospitalId,
          item.woundListId,
        );
        if (res.statusCode === 200) {
          updateData();
          return;
        } else {
          alert(res.statusCode + '\n' + res.message);
          return;
        }
      }
    }
  };

  const showConfirmModalWithContent = (content) => {
    setConfirmModalContent(content);
  };

  const reInvite = async () => {
    setLoading(true);
    const newFormData = new FormData();
    newFormData.append('email', email);
    newFormData.append('hospitalId', id);
    formData.append('updatebyEmail', localStorage.getItem('email'));

    const res = await HospitalApi.emailReinvte(newFormData);
    if (res.statusCode === 200) {
      setLoading(false);
      alert(`${res.message}`);
    } else {
      setLoading(false);
      alert(res.statusCode + '\n' + res.message);
    }
  };

  const editEmail = () => {
    showEmailModalWithContent('Change  Email Address');
    handleEmailModalOpen();
  };

  const handleValueChange = (value) => {
    setHospital((prevHospital) => ({
      ...prevHospital,
      email: value,
    }));
  };

  const handleEmailModalOpen = () => {
    setEmailModalStatus(true);
  };

  const handleEmailModalClose = () => setEmailModalStatus(false);

  const showEmailModalWithContent = (content) => {
    setEmailModalContent(content);
  };

  const setDate = (utcDate) => {
    const date = new Date(utcDate);
    date.setHours(date.getHours() + 9);
    return date.toISOString().replace('T', ' ').substring(0, 19);
  };

  return (
    <div>
      <style>
        {`
        .form-control::placeholder {
          color: #B6BCC5;
        }
        `}
      </style>
      <div
        style={{
          fontWeight: '700',
          fontSize: '30px',
          color: '#495057',
          marginBottom: '20px',
        }}
      >
        EDIT HOSPITAL INFORMATION
      </div>
      <Form onSubmit={updateHospital} onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}>
        <div
          style={{
            display: 'flex',
            width: '100%',
            gap: '20px',
          }}
        >
          <Form.Group style={{ width: '85%' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                paddingBottom: '5px',
                gap: '5px',
              }}
            >
              <span>Email</span>
              <button
                type="button"
                onClick={editEmail}
                style={{ padding: '0px' }}
              >
                <ModeEditOutlineOutlinedIcon
                  label="ModeEditOutlineOutlinedIcon"
                  style={{
                    color: '#495057',
                    opacity: '90%',
                    fontSize: '20px',
                  }}
                />
              </button>
            </div>
            <Form.Control
              disabled={true}
              style={{
                margin: '0px',
                color: '#B6bcd5',
              }}
              type="text"
              placeholder="Enter email..."
              name="email"
              autoComplete="off"
              onChange={handleValueChange}
              value={email}
              maxLength={100}
            />
          </Form.Group>
          <Form.Group style={{ width: '15%' }}>
            <span>Invitation Status</span>
            <Form.Control
              disabled={true}
              style={{
                margin: '0px',
                marginTop: '5px',
                color: '#B6bcd5',
              }}
              type="text"
              placeholder="Status"
              name="status"
              autoComplete="off"
              value={hospital.memberStatus}
            />
          </Form.Group>
        </div>
        <Form.Group>
          <span>Name</span>
          <span style={{ color: 'red' }}> *</span>
          <Form.Control
            style={{ margin: '0px' }}
            type="text"
            placeholder="Enter hospital name..."
            name="hospitalName"
            autoComplete="off"
            onChange={handleChange}
            value={hospital.hospitalName}
            maxLength={30}
          />
        </Form.Group>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Form.Group>
            <div style={{ width: '224px', marginRight: '15px' }}>
              <span>Hospital Country Code</span>
              <span style={{ color: 'red' }}> *</span>
              <AutoComplete
                size="small"
                disablePortal
                options={countryCodeName}
                name="countryCode"
                onChange={handleChangeCountryCode}
                value={hospital.countryCode || ''}
                sx={{ width: '100%' }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Enter country code..."
                    sx={{
                      '& legend': { display: 'none' },
                      '& fieldset': { top: 0 },
                    }}
                  />
                )}
              />
            </div>
          </Form.Group>
          <Form.Group style={{ width: '100%' }}>
            <span>Location</span>
            <Form.Control
              style={{ margin: '0px' }}
              type="text"
              placeholder="Enter hospital location..."
              name="location"
              autoComplete="off"
              onChange={handleChange}
              value={hospital.location || ''}
            />
          </Form.Group>
        </div>
        <Form.Group>
          <Form.Label>Product Name</Form.Label>
          <Form.Control
            style={{ margin: '0px' }}
            type="text"
            placeholder="Product Name"
            name="productName"
            autoComplete="off"
            value={hospital.productName || ''}
            disabled
          />
        </Form.Group>
        <Form.Group controlId="formBasicPassword">
          <Form.Label>Emblem/Profile Image</Form.Label>
          <div
            style={{
              width: '250px',
              height: '250px',
              border: '1px solid #B6BCC5',
              borderRadius: '4px',
              backgroundColor: '#FFF',
              boxSizing: 'border-box',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {emblemBlobUrl ? (
              <div
                style={{
                  position: 'relative',
                  width: '240px',
                  height: '240px',
                }}
              >
                <img
                  src={emblemBlobUrl}
                  alt=""
                  style={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                />
                <button
                  type="button"
                  style={{ position: 'absolute', top: '10px', right: '10px' }}
                  onClick={deleteImage}
                >
                  <CancelRoundedIcon
                    sx={{
                      fontSize: {
                        xs: '25px',
                      },
                      color: 'red',
                    }}
                  />
                </button>
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                }}
              >
                <div>
                  <CloudUploadIcon
                    sx={{
                      fontSize: {
                        xs: '50px',
                      },
                    }}
                  />
                </div>
                <div>Upload File</div>
                <div style={{ paddingTop: '10px', fontSize: '12px' }}>
                  *Max file size: 5MB
                </div>
                <div style={{ fontSize: '12px' }}>*JPG, GIF, PNG</div>
              </div>
            )}
          </div>
          <input
            style={{
              display: 'none',
            }}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            placeholder="CHOOSE IMAGES"
            ref={fileInputRef}
          />
          <button
            type="button"
            onClick={handleButtonClick}
            style={{
              marginLeft: '35px',
              marginTop: '10px',
              outline: 'none',
              width: '170px',
              height: '38px',
              background: '#fff',
              color: '#3f598c',
              fontSize: '15px',
              fontWeight: '500',
              borderRadius: '5px',
              border: '2px solid #3f598c',
              cursor: 'pointer',
            }}
          >
            CHOOSE IMAGES
          </button>
        </Form.Group>
        <Form.Label>Wound Type</Form.Label>
        <span style={{ color: 'red' }}> *</span>
        {woundType.map((woundList) => {
          const wound = woundList[Object.keys(woundList)[0]];
          const woundListId = Object.keys(woundList)[0];
          return (
            <div key={woundListId}>
              <div
                style={{
                  background: '#fff',
                  width: '100%',
                  padding: '20px 20px 10px 20px',
                  marginBottom: 30,
                  alignItems: 'center',
                  borderRadius: '10px',
                  flexDirection: 'row',
                  border: wound.togglenYn
                    ? '1px solid #3F598C'
                    : '1px solid #D9D9D9',
                }}
              >
                <FormControlLabel
                  style={{
                    userSelect: 'none',
                  }}
                  control={<IOSSwitch sx={{ m: 1 }} />}
                  label={
                    <div sx={{ fontSize: '24px', fontWeight: '500' }}>
                      {wound.woundName}
                    </div>
                  }
                  labelPlacement="start"
                  name={`${woundListId},togglenYn`}
                  checked={wound.togglenYn}
                  onChange={handleChangeWound}
                />
                <div
                  style={{
                    height: wound.togglenYn ? 'calc(9rem)' : '0',
                    opacity: wound.togglenYn ? '1' : '0',
                    overflow: 'hidden',
                    transition: 'all 0.5s',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-around',
                    }}
                  >
                    {['autoIssued', 'aiDrawYn', 'hiddenYn', 'disableYn'].map(
                      (key) => (
                        <FormControlLabel
                          key={key}
                          style={{
                            userSelect: 'none',
                          }}
                          control={
                            <Checkbox
                              sx={{
                                breakWord: 'break-all',
                                color: '#3F598C',
                                '&.Mui-checked': {
                                  color: '#3F598C',
                                },
                              }}
                              checked={wound[key]}
                              onChange={handleChangeWound}
                              name={`${woundListId},${key}`}
                            />
                          }
                          label={key.replace(/([A-Z])/g, ' $1')}
                        />
                      ),
                    )}
                  </div>
                  <Form.Group>
                    <AutoComplete
                      value={defaultTemplates[woundListId] || null}
                      disablePortal
                      options={templateName}
                      onChange={handleChangeTemplate(
                        `${woundListId},kitMetaId`,
                      )}
                      sx={{ width: '100%' }}
                      renderInput={(params) => (
                        <TextField {...params} label="Set Template" />
                      )}
                    />
                  </Form.Group>
                </div>
              </div>
            </div>
          );
        })}
        <div style={{ display: 'flex' }}>
          <Form.Group style={{ marginRight: '20px', width: '50%' }}>
            <Form.Label style={{ fontWeight: 'bold' }}>Insert Date</Form.Label>
            <div
              style={{
                border: '1px solid #3F598C',
                borderRadius: '5px',
                padding: '10px 0px 10px 10px',
                background: '#DADADA',
                minHeight: '40px',
              }}
            >
              {hospital.insertDate && (
                <div>{setDate(hospital.insertDate)} (KST)</div>
              )}
            </div>
          </Form.Group>
          <Form.Group style={{ marginLeft: '20px', width: '50%' }}>
            <Form.Label style={{ fontWeight: 'bold' }}>Insert ID</Form.Label>
            <div
              style={{
                border: '1px solid #3F598C',
                borderRadius: '5px',
                padding: '10px 0px 10px 10px',
                background: '#DADADA',
                minHeight: '40px',
              }}
            >
              {hospital.insertId}
            </div>
          </Form.Group>
        </div>
        <div style={{ display: 'flex' }}>
          <Form.Group style={{ marginRight: '20px', width: '50%' }}>
            <Form.Label style={{ fontWeight: 'bold' }}>Modify Date</Form.Label>
            <div
              style={{
                border: '1px solid #3F598C',
                borderRadius: '5px',
                padding: '10px 0px 10px 10px',
                background: '#DADADA',
                minHeight: '40px',
              }}
            >
              {hospital.insertDate && (
                <div>{setDate(hospital.modifyDate)} (KST)</div>
              )}
            </div>
          </Form.Group>
          <Form.Group style={{ marginLeft: '20px', width: '50%' }}>
            <Form.Label style={{ fontWeight: 'bold' }}>Modify ID</Form.Label>
            <div
              style={{
                border: '1px solid #3F598C',
                borderRadius: '5px',
                padding: '10px 0px 10px 10px',
                background: '#DADADA',
                minHeight: '40px',
              }}
            >
              {hospital.modifyId}
            </div>
          </Form.Group>
        </div>
        <Form.Group>
          <Form.Label>List of Doctors in the Hospital</Form.Label>
          <div style={{ height: 370, width: '100%' }}>
            <DataGrid
              rows={addRowsData(doctorList)}
              columns={addColumnsData(columns)}
              autoPageSize={true}
              disableColumnMenu
              disableSelectionOnClick
              isRowSelectable={() => false}
              sx={{
                boxShadow: 2,
                border: 2,
                borderColor: 'primary.light',
                '& .MuiDataGrid-cell:hover': {
                  color: 'primary.main',
                },
              }}
            />
          </div>
        </Form.Group>
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <button
            type="button"
            onClick={() => props.history.goBack()}
            style={{ marginRight: '25px', fontSize: '18px', fontWeight: '500' }}
          >
            BACK
          </button>
          <button
            className="blue"
            type="submit"
            style={{ maringLeft: '25px', fontSize: '18px', fontWeight: '500' }}
          >
            SAVE
          </button>
        </Grid>
      </Form>
      <div style={{ display: 'none' }}>
        <BasicModal
          open={modalStatus}
          handleModalClose={handleModalClose}
          content={modalContent}
        />
        <ConfirmModal
          open={modalConfirmStatus}
          handleModalClose={handleConfirmModalClose}
          handleConfirmModalClose={handleConfirmModalSet}
          content={modalConfirmContent}
        />
        <EmailModal
          open={emailModalStatus}
          handleModalClose={handleEmailModalClose}
          content={emailModalContent}
          email={hospital.email}
          setEmail={setEmail}
          hospitalId={hospital.hospitalId}
          role={'HospitalAdmin'}
        />
      </div>
      {loading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <MyLoading />
        </div>
      )}
    </div>
  );
};

export default HospitalUpdate;
